import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import BackToTop from '../../components/BackToTop';

const PrivacyPolicy = () => {
  const effectiveDate = 'January 1, 2026';

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <Header />
      <main className="flex-1 pt-20 sm:pt-24 md:pt-28">
        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 py-12 sm:py-16">
          <motion.h1
            className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold text-navy mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Privacy Policy
          </motion.h1>
          <motion.p
            className="font-body text-sm sm:text-base text-slate"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Effective Date: {effectiveDate}
          </motion.p>
        </section>

        {/* Content */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 pb-16 sm:pb-20">
          <motion.div
            className="prose prose-lg max-w-none"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1 }
              }
            }}
          >
            {/* Introduction */}
            <motion.div variants={fadeInUp} className="mb-10">
              <p className="font-body text-base sm:text-lg text-charcoal leading-relaxed">
                The Poetic Method™ ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy
                explains how we collect, use, disclose, and safeguard your information when you visit our website
                or engage with our services.
              </p>
            </motion.div>

            {/* Information We Collect */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Information We Collect</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-display text-lg text-teal mb-2">Personal Information</h3>
                  <p className="font-body text-base text-charcoal leading-relaxed">
                    When you contact us through our website, subscribe to our newsletter, or engage our services,
                    we may collect:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 font-body text-base text-charcoal">
                    <li>Name and job title</li>
                    <li>Email address</li>
                    <li>Organization name</li>
                    <li>Phone number (if provided)</li>
                    <li>Message content and inquiry details</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-display text-lg text-teal mb-2">Automatically Collected Information</h3>
                  <p className="font-body text-base text-charcoal leading-relaxed">
                    When you visit our website, we may automatically collect certain information, including:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 font-body text-base text-charcoal">
                    <li>IP address and browser type</li>
                    <li>Device information</li>
                    <li>Pages visited and time spent on site</li>
                    <li>Referring website or source</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* How We Use Your Information */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">How We Use Your Information</h2>
              <p className="font-body text-base text-charcoal leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-base text-charcoal">
                <li>Respond to your inquiries and provide requested services</li>
                <li>Send relevant communications about our services (with your consent)</li>
                <li>Improve our website and user experience</li>
                <li>Comply with legal obligations</li>
                <li>Protect against fraudulent or unauthorized activity</li>
              </ul>
            </motion.div>

            {/* Information Sharing */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Information Sharing</h2>
              <p className="font-body text-base text-charcoal leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your
                information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-base text-charcoal">
                <li>With service providers who assist in operating our website and delivering services</li>
                <li>When required by law or to protect our legal rights</li>
                <li>With your explicit consent</li>
              </ul>
            </motion.div>

            {/* Data Security */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Data Security</h2>
              <p className="font-body text-base text-charcoal leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information
                against unauthorized access, alteration, disclosure, or destruction. However, no method of
                transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </motion.div>

            {/* Your Rights */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Your Rights</h2>
              <p className="font-body text-base text-charcoal leading-relaxed mb-4">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-base text-charcoal">
                <li>Access and receive a copy of your personal data</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal data</li>
                <li>Object to or restrict processing of your data</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </motion.div>

            {/* Cookies */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Cookies and Tracking Technologies</h2>
              <p className="font-body text-base text-charcoal leading-relaxed">
                Our website may use cookies and similar tracking technologies to enhance your browsing experience.
                You can control cookie settings through your browser preferences. Disabling cookies may affect
                certain website functionality.
              </p>
            </motion.div>

            {/* Third-Party Links */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Third-Party Links</h2>
              <p className="font-body text-base text-charcoal leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy
                practices or content of these external sites. We encourage you to review the privacy policies
                of any third-party sites you visit.
              </p>
            </motion.div>

            {/* Children's Privacy */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Children's Privacy</h2>
              <p className="font-body text-base text-charcoal leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect
                personal information from children. If you believe we have inadvertently collected such information,
                please contact us immediately.
              </p>
            </motion.div>

            {/* Changes to This Policy */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Changes to This Privacy Policy</h2>
              <p className="font-body text-base text-charcoal leading-relaxed">
                We may update this Privacy Policy from time to time. The updated version will be indicated by
                an updated "Effective Date" at the top of this page. We encourage you to review this Privacy
                Policy periodically for any changes.
              </p>
            </motion.div>

            {/* Contact Us */}
            <motion.div variants={fadeInUp} className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-navy mb-4">Contact Us</h2>
              <p className="font-body text-base text-charcoal leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="bg-white p-6 rounded-lg border border-mist">
                <p className="font-body text-base text-charcoal">
                  <strong className="text-navy">The Poetic Method™</strong>
                </p>
                <p className="font-body text-base text-charcoal mt-2">
                  Email:{' '}
                  <a
                    href="mailto:poeticmethod101@gmail.com"
                    className="text-teal hover:underline transition-colors"
                  >
                    poeticmethod101@gmail.com
                  </a>
                </p>
                <p className="font-body text-base text-slate mt-1">
                  South Florida · Serving organizations nationwide
                </p>
              </div>
            </motion.div>
          </motion.div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default PrivacyPolicy;
